import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import "../styles/promptcrm-landing.css";

const stats = [
  { label: "Contacts managed", value: "42,000+" },
  { label: "Bounce rate eliminated", value: "40% → 0%" },
  { label: "Tools replaced", value: "3-in-1" }
];

const problemCards = [
  {
    title: "Three subscriptions to do one job",
    body:
      "A CRM for contacts. An email platform for campaigns. A scraping tool for leads. You're paying triple for a workflow that should be seamless.",
    cost: "Cost: ~$200-400/mo"
  },
  {
    title: "Mass email tools get flagged",
    body:
      "Traditional platforms send from shared servers. Email providers know this — and block them. Your carefully written outreach never reaches the inbox.",
    cost: "Cost: 20-40% bounce rates"
  },
  {
    title: "Hours wasted finding leads manually",
    body:
      "Copying names from LinkedIn. Pasting into spreadsheets. Searching for emails one by one. Your highest-paid people are doing data entry.",
    cost: "Cost: ~5-10 hrs/week per rep"
  }
];

const comparisonBefore = [
  "$99/mo paid for a standalone CRM",
  "$59/mo paid for an email campaign tool",
  "$99/mo paid for a LinkedIn lead scraper",
  "Emails bouncing due to shared sending IPs",
  "Manual data entry between disconnected apps"
];

const comparisonAfter = [
  "One subscription replaces all three tools",
  "Built-in LinkedIn scraper finds verified emails",
  "Native CRM manages contacts and pipelines",
  "Emails send directly from your real Gmail",
  "One connected workflow from lead to booked meeting"
];

const featureCards = [
  { icon: "💬", title: "Gmail-Native Sending", desc: "Emails go out from your real inbox — not a shared server. That's why they land in the primary tab, not spam." },
  { icon: "🔍", title: "LinkedIn Lead Scraper", desc: "Find and import contacts directly from LinkedIn with built-in data validation so you never waste a credit." },
  { icon: "🗂️", title: "Smart Contact Management", desc: "Auto-tag contacts as warm, cold, or active based on engagement. Dynamic segmentation that updates itself." },
  { icon: "⏸️", title: "Auto Reply Detection", desc: "When someone replies, campaigns pause automatically. No embarrassing duplicate follow-ups." },
  { icon: "👥", title: "Team Workspaces", desc: "Multiple team members running campaigns from one workspace with shared visibility and coordination." },
  { icon: "📊", title: "Campaign Analytics", desc: "Track opens, clicks, replies, and bounces across every campaign. Know what's working, fix what isn't." }
];

const steps = [
  { number: "01", title: "Connect your Gmail", desc: "Securely link your personal or business Gmail via OAuth. Your emails, your reputation, your deliverability — all yours." },
  { number: "02", title: "Build your contact list", desc: "Import from CSV, scrape from LinkedIn, or add manually. PromptCRM validates and deduplicates automatically." },
  { number: "03", title: "Launch campaigns", desc: "Write your sequence, set your schedule, and let PromptCRM send from your inbox while you focus on closing deals." }
];

const faqs = [
  {
    question: "Why does sending from Gmail matter?",
    answer:
      "Traditional email platforms send from shared servers that frequently get flagged as spam or promotional. Your Gmail is trusted and authenticated by Google. Sending natively is the #1 way to ensure your emails actually hit the primary inbox."
  },
  {
    question: "Will this work with Google Workspace?",
    answer:
      "Yes. It works seamlessly with both personal Gmail and business Google Workspace accounts. We connect securely via official Google OAuth — meaning we never see or store your password."
  },
  {
    question: "What's included in early access?",
    answer:
      "You get the full platform: the CRM, email campaign builder, LinkedIn scraper, team workspaces, and analytics. Founding members also get direct input into our product roadmap and lock in a permanent early pricing rate."
  },
  {
    question: "How is this different from HubSpot or Salesforce?",
    answer:
      "Those are enterprise tools built for massive organizations with complex reporting needs and massive budgets. PromptCRM is purpose-built for small, agile teams that just need to find leads, reach out, and book meetings — without the bloat or excessive cost."
  },
  {
    question: "Is my data safe?",
    answer:
      "Absolutely. We use OAuth for Gmail authentication (we never store passwords). All data is encrypted both in transit and at rest. Your contacts, leads, and campaigns belong exclusively to you."
  }
];

export default function LandingPage() {
  const { user } = useAuth();
  const primaryCta = user ? "/dashboard" : "/register";
  const secondaryCta = user ? "/dashboard" : "/login";

  const [heroSubmitted, setHeroSubmitted] = useState(false);
  const [finalSubmitted, setFinalSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState({ hero: false, final: false });
  const [submitError, setSubmitError] = useState({ hero: '', final: '' });
  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    const fadeElements = Array.from(document.querySelectorAll(".fade-up"));

    if (!("IntersectionObserver" in window)) {
      fadeElements.forEach((el) => el.classList.add("visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05 }
    );

    fadeElements.forEach((el) => observer.observe(el));

    const timer = setTimeout(() => {
      fadeElements.forEach((el) => el.classList.add("visible"));
    }, 1200);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, []);

  async function handleSubmit(e, setter, section) {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = (formData.get('email') || '').toString().trim();

    if (!email) {
      setSubmitError((prev) => ({ ...prev, [section]: 'Please enter a valid email.' }));
      return;
    }

    setSubmitError((prev) => ({ ...prev, [section]: '' }));
    setIsSubmitting((prev) => ({ ...prev, [section]: true }));

    try {
      const response = await fetch('/api/landing/early-access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          section,
          pageUrl: window.location.href
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success === false) {
        throw new Error(data?.error || 'Failed to save submission');
      }

      setter(true);
      form.reset();
    } catch (error) {
      console.error('Early access signup failed:', error);
      setSubmitError((prev) => ({
        ...prev,
        [section]: 'Something went wrong. Please try again.'
      }));
    } finally {
      setIsSubmitting((prev) => ({ ...prev, [section]: false }));
    }
  }

  return (
    <div className="landing-page">
      <nav className="sticky-nav">
        <div className="container nav-content">
          <Link to="/" className="logo">
            PromptCRM<span>.ai</span>
          </Link>
          <Link to={primaryCta} className="btn">
            {user ? "Open Dashboard" : "Get Early Access"}
          </Link>
        </div>
      </nav>

      <header className="hero container animate-on-load">
        <div className="pill-badge">
          <span role="img" aria-label="announcement">
            📢
          </span>{" "}
          Now accepting early access applications
        </div>
        <h1>Stop paying for three tools to send emails that land in spam</h1>
        <p className="subheadline">
          PromptCRM replaces your CRM, email platform, and lead scraper with one app — and sends from your real Gmail so your outreach actually gets read.
        </p>

        <div className="email-form-container">
          {heroSubmitted ? (
            <div className="success-message">🎉 You're on the list. We'll be in touch soon.</div>
          ) : (
            <form className="email-form" onSubmit={(e) => handleSubmit(e, setHeroSubmitted, 'hero')}>
              <input type="email" name="email" required placeholder="Enter your work email" className="email-input" aria-label="Email address" />
              <button type="submit" className="btn" disabled={isSubmitting.hero}>
                {isSubmitting.hero ? 'Submitting...' : 'Get Early Access →'}
              </button>
            </form>
          )}
          {submitError.hero && <p className="micro-copy" role="alert">{submitError.hero}</p>}
          <p className="micro-copy">Early access pricing locked in for founding members</p>
        </div>
      </header>

      <section className="social-proof animate-on-load delay-1">
        <div className="container">
          <div className="social-proof-label">Built in production, not in theory</div>
          <div className="stats-grid">
            {stats.map((stat) => (
              <div className="stat-item" key={stat.label}>
                <span className="stat-number serif">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="problem-section container fade-up">
        <div className="problem-header">
          <span className="section-label">The Problem</span>
          <h2>Your outreach stack is bleeding money and killing deliverability</h2>
          <p>Small teams are paying $300+/month across multiple platforms — and still watching emails bounce, land in spam, or get blocked by servers.</p>
        </div>

        <div className="cards-grid">
          {problemCards.map((card) => (
            <div className="problem-card" key={card.title}>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
              <div className="cost-tag">{card.cost}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="comparison-section fade-up">
        <div className="container">
          <span className="section-label">The Solution</span>
          <h2>One platform. Everything you need to book meetings.</h2>

          <div className="comparison-grid">
            <div className="comparison-col col-before">
              <div className="comparison-title">Before PromptCRM</div>
              <ul className="comparison-list">
                {comparisonBefore.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="comparison-col col-after">
              <div className="comparison-title">With PromptCRM</div>
              <ul className="comparison-list">
                {comparisonAfter.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="case-study container fade-up">
        <div className="case-card">
          <span className="section-label">Real Results</span>
          <p className="quote serif">
            “We had a contact list with a 40% bounce rate on Mailshake. Same list, same contacts — we moved it to PromptCRM and started booking appointments within the first week.”
          </p>
          <div className="metrics-flow">
            <div className="metric-box metric-before">40% bounced</div>
            <div className="metric-arrow">→</div>
            <div className="metric-box metric-after">Booking meetings</div>
          </div>
        </div>
      </section>

      <section className="features fade-up">
        <div className="container">
          <span className="section-label">Features</span>
          <h2>Everything your outreach needs. Nothing it doesn't.</h2>

          <div className="features-grid">
            {featureCards.map((feature) => (
              <div className="feature-card" key={feature.title}>
                <div className="feature-icon" aria-hidden="true">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="how-it-works container fade-up">
        <span className="section-label">How it works</span>
        <h2>From zero to booked meetings in three steps</h2>

        <div className="steps-container">
          {steps.map((step) => (
            <div className="step" key={step.number}>
              <div className="step-number">{step.number}</div>
              <div className="step-content">
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing-teaser fade-up">
        <div className="container">
          <span className="section-label">Early Access Pricing</span>
          <h2>Replace three tools with one — for less than any of them cost alone</h2>

          <div className="pricing-visual">
            <div className="price-competitors">
              <div className="price-box-old">$99 CRM</div>
              <div className="price-box-old">$59 Email Tool</div>
              <div className="price-box-old">$99 Scraper</div>
            </div>

            <div className="price-arrow">→</div>

            <div className="price-box-new">
              <h3>One plan. One price.</h3>
              <p>Details shared with early access members</p>
              <div className="price-badge">✓ Price locked forever</div>
            </div>
          </div>

          <p>No contracts. Cancel anytime. Founding member rate never expires.</p>
        </div>
      </section>

      <section className="faq-section container fade-up">
        <span className="section-label">FAQ</span>
        <h2>Questions? Covered.</h2>

        <div className="faq-container">
          {faqs.map((item, index) => {
            const isActive = activeFaq === index;
            return (
              <div className={`faq-item ${isActive ? "active" : ""}`} key={item.question}>
                <button className="faq-question" type="button" onClick={() => setActiveFaq(isActive ? null : index)}>
                  {item.question}
                  <span className="faq-icon">{isActive ? "×" : "+"}</span>
                </button>
                <div className="faq-answer" style={isActive ? { maxHeight: 500, paddingBottom: "1.5rem" } : undefined}>
                  <p>{item.answer}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section id="early-access" className="final-cta fade-up">
        <div className="container">
          <h2 className="serif">Your outreach deserves to actually get read</h2>
          <p>Join the founding members getting early access to the all-in-one outreach platform that doesn't get blocked.</p>

          <div className="email-form-container">
            {finalSubmitted ? (
              <div className="success-message">🎉 You're on the list. We'll be in touch soon.</div>
            ) : (
              <form className="email-form" onSubmit={(e) => handleSubmit(e, setFinalSubmitted, 'final')}>
                <input type="email" name="email" required placeholder="Enter your work email" className="email-input" aria-label="Email address" />
                <button type="submit" className="btn" disabled={isSubmitting.final}>
                  {isSubmitting.final ? 'Submitting...' : 'Get Early Access →'}
                </button>
              </form>
            )}
            {submitError.final && <p className="micro-copy" role="alert">{submitError.final}</p>}
            <p className="micro-copy">Limited founding member spots available</p>
          </div>
        </div>
      </section>
    </div>
  );
}
